import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import roaryMascot from '/assets/Roary-mascot.png';

interface RoaryEntryCardProps {
  onStart: () => void;
}

// Fire particle animation for background
const FireParticles: React.FC = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(6)].map((_, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 bg-orange-400 rounded-full opacity-60 animate-roary-float-up"
        style={{
          left: `${20 + i * 12}%`,
          bottom: '20%',
          animationDuration: `${2 + i * 0.3}s`,
          animationDelay: `${i * 0.4}s`,
        }}
      />
    ))}
  </div>
);

export const RoaryEntryCard: React.FC<RoaryEntryCardProps> = ({ onStart }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="w-full my-8">
      {/* Separator */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
        <Sparkles className="w-4 h-4 text-orange-500" />
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
      </div>

      {/* Main Card */}
      <div
        className={`
          relative overflow-hidden rounded-2xl p-8
          bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900
          border border-slate-700/50
          shadow-xl shadow-orange-500/10
          transition-all duration-500
          ${isHovered ? 'shadow-2xl shadow-orange-500/20 scale-[1.01]' : ''}
        `}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Background pattern - dragon scales */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0C13.4 0 0 13.4 0 30c0-16.6 13.4-30 30-30zm0 60C46.6 60 60 46.6 60 30c0 16.6-13.4 30-30 30z' fill='%23f97316' fill-opacity='0.4'/%3E%3C/svg%3E")`,
            backgroundSize: '30px 30px',
          }}
        />

        {/* Gradient overlay */}
        <div
          className={`
            absolute inset-0 opacity-0 transition-opacity duration-500
            bg-gradient-to-r from-orange-500/10 via-purple-500/10 to-orange-500/10
            ${isHovered ? 'opacity-100' : ''}
          `}
        />

        {/* Fire particles */}
        <FireParticles />

        {/* Content */}
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Left side - Text content */}
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-5 mb-4">
              <div
                className={`
                  flex-shrink-0
                  transition-transform duration-300
                  ${isHovered ? 'scale-110' : ''}
                `}
              >
                <img
                  src={roaryMascot}
                  alt="Roary mascot"
                  className="w-32 h-32 object-contain drop-shadow-lg"
                />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">Ask Roary</h2>
            </div>

            <p className="text-gray-300 text-lg mb-2">
              The world's first website-building dragon. Chat with Roary, tell him about your
              business, and watch him build your dream website.
            </p>
            <p className="text-gray-400">
              No templates. No limits. Just a friendly dragon and a conversation.
            </p>
          </div>

          {/* Right side - CTA Button */}
          <div className="flex-shrink-0">
            <button
              onClick={onStart}
              className={`
                group relative px-8 py-4 rounded-xl
                bg-gradient-to-r from-orange-500 to-orange-600
                hover:from-orange-400 hover:to-orange-500
                text-white font-semibold text-lg
                shadow-lg shadow-orange-500/30
                transition-all duration-300
                ${isHovered ? 'shadow-xl shadow-orange-500/40' : ''}
              `}
            >
              <span className="relative z-10 flex items-center gap-2">
                Ask Roary
                <svg
                  className={`w-5 h-5 transition-transform duration-300 ${isHovered ? 'translate-x-1' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </span>

              {/* Button glow effect */}
              <div
                className={`
                  absolute inset-0 rounded-xl
                  bg-gradient-to-r from-orange-400 to-orange-500
                  opacity-0 blur-xl transition-opacity duration-300
                  ${isHovered ? 'opacity-50' : ''}
                `}
              />
            </button>
          </div>
        </div>

        {/* Suggestion text */}
        <div className="relative z-10 mt-6 pt-6 border-t border-slate-700/50">
          <p className="text-gray-500 text-sm flex items-center gap-2 justify-center md:justify-start">
            <span className="text-orange-400">💡</span>
            Try: "I need a website for my dance studio" or "Help me build a portfolio site"
          </p>
        </div>
      </div>
    </div>
  );
};
